import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import { Box, Grid, IconButton, Stack, Skeleton, } from '@mui/material';
import { ChevronRight as OpenIcon, ChevronLeft as HideIcon, } from '@mui/icons-material';
import { flattenSections, useStore, useTokens, } from '@mcn-platform/models';
import { WysiwygViewer } from '../../publisher/wysiwyg/WysiwygEditor';
import CourseBar from './courseBar/CourseBar';
import Notes from './notes/Notes';
// import Tabs from './Tabs';
// import CourseNavigator from '../CourseNavigator';
import HeaderWithPrevNext from './headerWithPagination/HeaderWithPrevNext';
import LessonPanel from './lessonPanel/LessonPanel';
import MobileCourseOutline from './MobileCourseOutlineNav';
import KickerWidget from '../kicker/KickerWidget';
import formatSeconds from '../common/formatSeconds';
import PlaceHolder from '../common/PlaceHolder';
import SectionsPanel from './sectionsPanel/SectionsPanel';
import ScormPlayer from '../common/scorm/ScormPlayer';
import UpNext from './UpNext';
import NotAuthorizedBanner from '../common/NotAuthorizedBanner';
// Maybe this default should be on microcasting.schema
const defaultPrerequisiteMessage = [
    {
        id: 'a18rn',
        children: [
            {
                bold: true,
                text: 'You cannot access this course yet.',
            },
            {
                text: '\nTo unlock this course, you need to complete the required prerequisites.\n\n',
            },
            {
                text: 'Make sure to review your progress and complete any required courses or certifications.',
            },
        ],
        type: 'p',
    },
];
const Class = ({ courseID, data, settings, editData, }) => {
    // I think both currentPosition and seekPosition are important
    // because there may be a condition
    // when reference creation in-progress by user meanwhile he also viewing other reference video
    const [currentPosition, setCurrentPosition] = useState(0);
    const [seekPosition, setSeekPosition] = useState(null);
    const [maxPosition, setMaxPosition] = useState(null);
    const [seekTrigger, setSeekTrigger] = useState(null);
    const [courseSections, setCourseSections] = useState([]);
    const [showOutline, setShowOutline] = useState(true);
    const [course, setCourse] = useState();
    const [courseSection, internalSetCourseSection] = useState();
    const [courseProgress, setCourseProgress] = useState({});
    const [notAuthorized, setNotAuthorized] = useState(false);
    const [prerequisiteMessage, setPrerequisiteMessage] = useState(null);
    const [kicker, setKicker] = useState();
    const [isMobileView, setIsMobileView] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const store = useStore();
    const tokens = useTokens();
    const { modifiers } = data;
    const { currentPortal, isAnonymous } = store;
    const playerContainerRef = React.useRef(null);
    const itemID = Array.isArray(modifiers) && modifiers.length > 1 ? modifiers[1] : null;
    const setActiveCourseSection = useCallback((newCourseSection) => {
        internalSetCourseSection(newCourseSection);
        if (!newCourseSection || !course) {
            return;
        }
        if (newCourseSection.type === 'microcast') {
            setKicker(newCourseSection.item?.microcast?.kicker);
        }
        if (newCourseSection.item?.kicker?.id) {
            setKicker(newCourseSection.item?.kicker);
        }
        else if (course.kicker?.id) {
            setKicker(course.kicker);
        }
        else {
            setKicker(null);
        }
        console.log('courseProgress:', { newCourseSection, courseProgress });
        if (!courseProgress) {
            return;
        }
        const lessonProgress = courseProgress.lessonProgress?.[newCourseSection.id];
        if (lessonProgress) {
            // if autoplay is enabled, don't set the seek position - always start at the beginning
            if (course.enableAutoplay) {
                setSeekPosition(null);
                setCurrentPosition(0);
            }
            else {
                setSeekPosition(lessonProgress.lastPosition || null);
                setCurrentPosition(lessonProgress.lastPosition || 0);
            }
            // console.log('new positions', { id: newCourseSection.id, lessonProgress, newMaxPosition });
            const newMaxPosition = lessonProgress.maxPosition || 0;
            setMaxPosition(newMaxPosition);
        }
        else {
            setSeekPosition(null);
            setCurrentPosition(0);
            setMaxPosition(0);
        }
    }, [course, courseProgress]);
    // TODO - this probably shouldn't happen this way but its a start for now
    // (can do a single call to load all the data for the course and the current user)
    // called when courseID changes, but not if courseSession changes
    useEffect(() => {
        const startCourse = async () => {
            if (courseID) {
                setIsLoading(true);
                let newCourse;
                try {
                    newCourse = await store.load('course', courseID);
                    const resp = await store.startCourse(newCourse);
                    setCourse(newCourse);
                    setCourseProgress(resp);
                    if (!resp) {
                        console.log('didnt get a course progress - no access to take this course');
                        setNotAuthorized(true);
                    }
                    else {
                        setNotAuthorized(false);
                    }
                    console.log('startClass complete', resp);
                }
                catch (error) {
                    console.error('Failed to startClass:', error);
                    if (error?.statusCode && error.statusCode !== 200) {
                        setNotAuthorized(true);
                        if (error.statusCode === 403 && error?.message?.startsWith('You do not meet the prerequisites')) {
                            setPrerequisiteMessage(newCourse?.prerequisiteMessage?.slate
                                || defaultPrerequisiteMessage);
                            setCourseProgress({
                                message: error.message,
                            });
                        }
                        else {
                            console.log('Generic access deny, showing default unauthorized state.');
                        }
                    }
                }
                finally {
                    setIsLoading(false);
                }
            }
            else {
                console.log('No course specified', courseID);
            }
        };
        console.log('calling startCourse', courseID);
        startCourse();
    }, [store, courseID]);
    // called when course section changes to update querystring
    useEffect(() => {
        if (courseSection) {
            const params = new URLSearchParams(searchParams);
            params.set('item', courseSection.id);
            setSearchParams(params, { replace: true });
        }
    }, [courseSection, searchParams, setSearchParams]);
    useEffect(() => {
        if (course?.sections?.length && course?.sections?.length > 0) {
            const courseData = flattenSections(course)
                .filter((section) => !section.hidden);
            setCourseSections(courseData);
            const param = searchParams.get('item') || itemID;
            if (param && course) {
                const item = course.findItemById(param);
                if (item.type === 'section') {
                    const firstNonContainerItem = course.findFirstNonContainerItem(item.section);
                    setActiveCourseSection(firstNonContainerItem);
                }
                else if (item.type === 'item') {
                    setActiveCourseSection(item.section);
                }
                console.log('setActiveCourseSection successful');
                return;
            }
            console.log('Error: itemID not found. Will load first non container item.');
            const first = courseData.find((d) => d.type !== 'container');
            console.log('first', first);
            setActiveCourseSection(first);
        }
    }, [
        course,
        course?.sections,
        course?.sections?.length,
        searchParams,
        itemID,
        setActiveCourseSection,
    ]);
    const setSelectedCourseSection = (item) => {
        const elem = document.getElementById('section-panel');
        if (elem) {
            elem.scrollTop = 0;
        }
        window.scrollTo(0, 0);
        setActiveCourseSection(item);
    };
    const updateCourseProgress = async (item, progress, lastPosition, newMaxPosition) => {
        console.log('UpdateCourseProgress called', {
            id: item.id,
            lastPosition,
            newMaxPosition,
        });
        if (course && item) {
            setCurrentPosition(lastPosition);
            setMaxPosition(newMaxPosition);
            // console.log('new positions', { lastPosition, newMaxPosition });
            const updatedCourseProgress = await store.trackCourseItemProgress(course.key, item.id, progress, lastPosition, newMaxPosition);
            setCourseProgress(updatedCourseProgress);
        }
    };
    const handleQuizAttemptComplete = async (item) => {
        const existingProgress = courseProgress?.lessonProgress?.[item.id]?.progress ?? 0;
        await updateCourseProgress(item, existingProgress, 0, 0);
    };
    const handleViewNote = (note) => {
        const { reference } = note;
        const courseItemDetails = reference?.split('/') || [];
        const courseItemID = courseItemDetails[1];
        //   const courseItemType = courseItemDetails[2];
        setActiveCourseSection(courseSections.find((l) => l.id === courseItemID));
        setSeekPosition(parseInt(courseItemDetails[3], 10));
        setSeekTrigger(Date.now());
        // @ts-expect-error Fix the type on the ref and this should work
        playerContainerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    // it can be an array of name/id pairs
    const makeReference = () => ([
        `${course?.id}/${courseSection?.id}/video/${currentPosition}`,
        `${course?.name}/${courseSection?.name}/Video/${formatSeconds(currentPosition)}`,
    ]);
    const nextSection = () => {
        let currIndex = courseSections.findIndex((item) => (item.id === courseSection?.id));
        // skip over section headers
        while (currIndex < (courseSections.length - 1)) {
            currIndex += 1;
            if (courseSections[currIndex].type !== 'container' && !courseSections[currIndex]?.item?.hidden) {
                return courseSections[currIndex];
            }
        }
        return undefined;
    };
    const prevSection = () => {
        let currIndex = courseSections.findIndex((item) => (item.id === courseSection?.id));
        // skip over section headers
        while (currIndex >= 1) {
            currIndex -= 1;
            if (courseSections[currIndex].type !== 'container' && !courseSections[currIndex]?.item?.hidden) {
                return courseSections[currIndex];
            }
        }
        return undefined;
    };
    const handleNextPrevious = (direction) => {
        const newSection = direction === 'next' ? nextSection() : prevSection();
        if (newSection) {
            // if (hasPlayedVideo && course?.enableAutoplay) {
            //   console.log('setting autoPlay to true');
            //   setAutoPlayVideo(true);
            // } else {
            //   setAutoPlayVideo(false);
            // }
            setActiveCourseSection(newSection);
        }
    };
    const handleResize = () => {
        const windowWidth = window.innerWidth;
        if (windowWidth <= 600 && !isMobileView) {
            setIsMobileView(true);
        }
        else if (windowWidth > 600 && !isMobileView) {
            setIsMobileView(false);
        }
    };
    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        handleResize();
    });
    if (editData && !course) {
        return _jsx(PlaceHolder, { placeHolderName: "Select course to see preview" });
    }
    if (isLoading || (course?.syncState !== 'loaded' && !notAuthorized)) {
        return (_jsxs(Stack, { direction: "row", height: "100%", width: "100%", spacing: 2, padding: 2, children: [_jsxs(Stack, { spacing: 1, width: "20%", children: [_jsx(Skeleton, { variant: "text", width: "80%", height: 32 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 })] }), _jsxs(Stack, { spacing: 2, width: "60%", children: [_jsx(Skeleton, { variant: "text", width: "60%", height: 32 }), _jsx(Skeleton, { variant: "text", width: "80%", height: 24 }), _jsx(Skeleton, { variant: "text", width: "90%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 300 })] }), _jsxs(Stack, { spacing: 1, width: "20%", children: [_jsx(Skeleton, { variant: "text", width: "60%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 48 })] })] }));
    }
    if (course?.format === 'scorm12' && courseProgress && !notAuthorized) {
        return (_jsx(ScormPlayer, { itemType: "course", item: course, itemProgress: courseProgress }));
    }
    const showRightSidePanel = !isMobileView && ((settings?.showUpNext && nextSection())
        || kicker?.id
        || (!isAnonymous && settings?.showNotes && courseSection?.id));
    const visibleSections = (course?.sections || []).filter((section) => !section?.item?.hidden);
    if (prerequisiteMessage) {
        return (_jsx(Box, { sx: {
                padding: 2,
                textAlign: 'center',
                maxWidth: 600,
                margin: '0 auto',
            }, children: _jsx(WysiwygViewer, { id: "prerequisiteMessage", value: prerequisiteMessage }) }));
    }
    if (notAuthorized) {
        return _jsx(NotAuthorizedBanner, { currentPortal: currentPortal });
    }
    const labelPrefix = !settings?.hideLessonLabel
        ? `${tokens.getToken('lessonName')}:`
        : '';
    return (_jsxs(Box, { sx: {
            height: '100%',
            overflow: 'hidden',
            paddingBottom: '60px',
        }, children: [_jsx(CourseBar, { courseName: course?.name || '', settings: {
                    showProgress: settings?.showProgressBar,
                }, progress: courseProgress?.progress ? courseProgress.progress : 0 }), _jsxs(Stack, { direction: "row", height: "100%", children: [!isMobileView && settings.outlineCollapsible && !showOutline && (_jsx(Box, { sx: {
                            width: '44px',
                            height: '100%',
                            overflowY: 'hidden',
                            backgroundImage: (t) => (`linear-gradient(0deg, ${t.palette.subNav.bottomColor}, ${t.palette.subNav.topColor})`),
                            marginRight: 2,
                        }, children: _jsx(Box, { sx: { overflowY: 'auto', height: '100%', position: 'absolute' }, children: settings.outlineCollapsible && (_jsx(Box, { sx: {
                                    width: '100%',
                                    textAlign: 'right',
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: (t) => (t?.palette?.subNav?.topColor),
                                    zIndex: 2,
                                    p: '4px',
                                    height: '30px',
                                }, children: _jsx(IconButton, { size: "small", className: "primary", onClick: () => setShowOutline(true), children: _jsx(OpenIcon, {}) }) })) }) })), _jsxs(Grid, { container: true, spacing: 2, sx: {
                            flexGrow: 1,
                            mt: 0,
                            height: '100%',
                            overflowY: 'hidden',
                        }, children: [!isMobileView && showOutline && (_jsx(Grid, { item: true, xs: 12, md: 3, order: { xs: 1, md: 0 }, sx: {
                                    height: '100%',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    pt: '0 !important',
                                    position: 'relative',
                                }, 
                                // causing the elements to stack instead of go side by side at certain breakpoints
                                // sx={{
                                //   minWidth: {
                                //     // for small screens we can may use left side MUI drawer
                                //     // https://mui.com/components/drawers/
                                //     xs: '100%', // theme.breakpoints.up('xs')
                                //     sm: 320, // theme.breakpoints.up('sm')
                                //   },
                                // }}
                                ref: playerContainerRef, children: _jsx(Box, { sx: {
                                        width: '100%',
                                        height: '100%',
                                        overflowY: 'hidden',
                                        backgroundImage: (t) => (`linear-gradient(0deg, ${t.palette.subNav.bottomColor}, ${t.palette.subNav.topColor})`),
                                    }, children: _jsxs(Box, { sx: { overflowY: 'auto', height: '100%' }, children: [settings.outlineCollapsible && (_jsx(Box, { sx: {
                                                    width: '100%',
                                                    textAlign: 'right',
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: (t) => (t?.palette?.subNav?.topColor),
                                                    zIndex: 2,
                                                    p: '4px',
                                                    height: '30px',
                                                }, children: _jsx(IconButton, { size: "small", className: "primary", onClick: () => setShowOutline(false), children: _jsx(HideIcon, {}) }) })), _jsx(SectionsPanel, { settings: {
                                                    sectionExpansionOption: settings?.sectionExpansionOption,
                                                    showLessonType: settings?.showLessonType,
                                                }, sections: visibleSections, selectedItem: courseSection, setSelectedItem: setSelectedCourseSection, courseProgress: courseProgress })] }) }) })), _jsx(Grid, { item: true, xs: 12, md: showOutline ? 9 : 12, order: { xs: 0, md: 1 }, sx: {
                                    p: 2,
                                    height: '100%',
                                    overflow: 'auto',
                                    pt: 0,
                                    position: 'relative',
                                }, id: "section-panel", children: _jsxs(Grid, { container: true, spacing: 2, sx: {
                                        ml: { xs: 0, md: '-16px' },
                                        width: 'auto',
                                    }, children: [_jsxs(Grid, { item: true, container: true, sx: { display: isMobileView ? 'flex' : '', flexWrap: isMobileView ? 'nowrap' : '' }, children: [isMobileView && (_jsx(Box, { sx: {
                                                        borderBottom: '2px solid #91bfec',
                                                        pr: 2,
                                                    }, children: _jsx(MobileCourseOutline
                                                    // settings={{ defaultExpanded: true }}
                                                    // sections={course.sections}
                                                    , { 
                                                        // settings={{ defaultExpanded: true }}
                                                        // sections={course.sections}
                                                        selectedItem: courseSection, setSelectedItem: setSelectedCourseSection, courseProgress: courseProgress, courseID: courseID }) })), _jsx(Grid, { item: true, xs: 12, children: courseSection
                                                        && (_jsx(HeaderWithPrevNext, { labelText: labelPrefix, labelInfo: courseSection?.name, goNext: () => handleNextPrevious('next'), goPrev: () => handleNextPrevious('previous'), hasNext: !!nextSection(), hasPrev: !!prevSection(), isMobileView: isMobileView })) })] }), _jsx(Grid, { item: true, xs: 12, md: showRightSidePanel ? 9 : 12, sx: courseSection?.type === 'html' ? { minWidth: 0 } : undefined, children: course && courseSection && (_jsx(LessonPanel, { course: course, courseSection: courseSection, courseProgress: courseProgress, currentPosition: currentPosition, maxPosition: maxPosition, setCurrentPosition: setCurrentPosition, seekPosition: seekPosition, seekTrigger: seekTrigger, handleNextPrevious: handleNextPrevious, updateProgress: updateCourseProgress, onQuizAttemptComplete: handleQuizAttemptComplete, isMobileView: isMobileView, settings: {
                                                    showMakeANoteBtn: settings?.showNotes,
                                                    showPlayerControls: true,
                                                    showActionBtns: true,
                                                    showReportIssueBtn: false,
                                                    showCTABtn: true,
                                                    showInfoTabs: true,
                                                    showTranscriptTab: settings?.showTranscriptTab,
                                                    showOverviewTab: settings?.showCourseDesc,
                                                    showMaterialTab: settings?.showCourseMaterials,
                                                    showQnATab: settings?.showQnATab,
                                                    hideLessonLabel: settings?.hideLessonLabel,
                                                } })) }), !isMobileView && (_jsx(Grid, { item: true, xs: 12, md: 3, children: _jsxs(Stack, { spacing: 1, direction: "column", children: [settings?.showUpNext && (_jsx(UpNext, { nextSection: nextSection(), goToNextSection: () => setSelectedCourseSection(nextSection()) })), _jsx(KickerWidget, { kickerID: kicker?.id, limitToPageWidth: true }), !isAnonymous && settings?.showNotes && (_jsx(Notes, { handleViewNote: handleViewNote, makeReference: makeReference, isCourseItemSelected: !!courseSection?.id, progress: courseProgress, modelName: "courseProgress" }))] }) }))] }) })] })] })] }));
};
Class.defaultProps = {
    editData: undefined,
    data: undefined,
};
Class.meta = {
    group: 'deliveryPages',
    name: 'Course Delivery Panel',
    description: 'Displays the full course delivery interface.',
    props: [
        {
            courseID: 'courseID',
            returnId: true, // hack to to make the picker return the course ID
            name: 'Course',
            type: 'course', // note this is a special type recognized by the system
            description: 'The course being delivered.',
        },
        {
            key: 'settings.sectionExpansionOption',
            name: 'Lesson Outline Sections',
            type: 'select',
            description: 'Choose how the lesson outline sections are displayed.',
            values: ['firstOpen', 'allCollapsed', 'allExpanded'],
            labels: ['First Section Expanded', 'All Collapsed', 'All Expanded'],
        },
        {
            key: 'settings.outlineCollapsible',
            name: 'Outline is collapsible',
            type: 'boolean',
            description: 'The course outline can be open and closed',
        },
        {
            key: 'settings.showUpNext',
            name: 'Show Up Next in Right Panel',
            type: 'boolean',
            description: 'Shows the Up Next card in the right panel',
        },
        {
            key: 'settings.showNotes',
            name: 'Show Notes',
            type: 'boolean',
            description: 'Show the notes section',
        },
        {
            key: 'settings.showProgressBar',
            name: 'Show Progress Bar',
            type: 'boolean',
            description: 'Show the progress bar',
        },
        {
            key: 'settings.showTranscriptTab',
            name: 'Show Transcript Tab',
            type: 'boolean',
            description: 'Show the transcript tab',
        },
        {
            key: 'settings.showQnATab',
            name: 'Show Q&A Tab',
            type: 'boolean',
            description: 'Show the Q&A tab.',
        },
        {
            key: 'settings.showCourseDesc',
            name: 'Show Course Description',
            type: 'boolean',
            description: 'Show the course description',
        },
        {
            key: 'settings.showCourseMaterials',
            name: 'Show Course Materials',
            type: 'boolean',
            description: 'Show the course materials section',
        },
        {
            key: 'settings.showLessonType',
            name: 'Show Lesson Type in Outline',
            type: 'boolean',
            description: 'Show the Lesson Type in Outline',
        },
        {
            key: 'settings.hideLessonLabel',
            name: 'Hide Label Prefix for Lesson Title',
            type: 'boolean',
            description: "If enabled, it removes the 'Lesson:' prefix before the lesson title.",
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'Class',
        props: {
            courseKey: '@courseKey',
            courseID: '@courseID',
            settings: {
                outlineColapsible: false,
                sectionExpansionOption: 'firstOpen',
                showUpNext: false,
                showNotes: true,
                showProgressBar: true,
                showTranscriptTab: true,
                showQnATab: true,
                showCourseDesc: true,
                showCourseMaterials: true,
                showLessonType: true,
                hideLessonLabel: false,
            },
        },
    },
};
export default observer(Class);
//# sourceMappingURL=Class.js.map